import dayjs from "dayjs";

export const capitalizeWords = (text) => {
  if (!text) return "";

  return String(text)
    .toLowerCase()
    .split(" ")
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};


export const formatCnpj = (value) => {
  if (!value) return "";

  const numbers = String(value).replace(/\D/g, "");

  if (numbers.length !== 14) return value;

  return numbers.replace(
    /^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/,
    "$1.$2.$3/$4-$5"
  );
};

export const parseNfeXml = (xmlText, t) => {
  const parser = new DOMParser();
  const xmlDoc = parser.parseFromString(xmlText, "application/xml");

  const parserError = xmlDoc.getElementsByTagName("parsererror");
  if (parserError.length > 0) {
    throw new Error(t("message.msg0014"));
  }

  const getText = (parent, tag) =>
    parent?.getElementsByTagName(tag)?.[0]?.textContent?.trim() ?? "";

  const getDirectChild = (parent, tag) =>
    Array.from(parent?.children || []).find((child) => child.tagName === tag);

  const infNFe = xmlDoc.getElementsByTagName("infNFe")[0];
  const ide = xmlDoc.getElementsByTagName("ide")[0];
  const emit = xmlDoc.getElementsByTagName("emit")[0];
  const dest = xmlDoc.getElementsByTagName("dest")[0];
  // const protNFe = xmlDoc.getElementsByTagName("protNFe")[0];
  const infProt = xmlDoc.getElementsByTagName("infProt")[0];
  const total = xmlDoc.getElementsByTagName("ICMSTot")[0];
  const transp = xmlDoc.getElementsByTagName("transp")[0];
  const transporta = xmlDoc.getElementsByTagName("transporta")[0];
  const veicTransp = xmlDoc.getElementsByTagName("veicTransp")[0];
  const vol = xmlDoc.getElementsByTagName("vol")[0];
  // const cobr = xmlDoc.getElementsByTagName("cobr")[0];
  const infAdic = xmlDoc.getElementsByTagName("infAdic")[0];

  const enderEmit = emit?.getElementsByTagName("enderEmit")[0];
  const enderDest = dest?.getElementsByTagName("enderDest")[0];

  const dhEmi = getText(ide, "dhEmi");
  const dhSaiEnt = getText(ide, "dhSaiEnt");

  const issueDate = dhEmi ? dhEmi.split("T")[0] : "";
  const issueTime = dhEmi ? dhEmi.split("T")[1]?.substring(0, 8) : "";
  const outDate = dhSaiEnt ? dhSaiEnt.split("T")[0] : "";
  const outTime = dhSaiEnt ? dhSaiEnt.split("T")[1]?.substring(0, 8) : "";

  const buildAddress = (ender) => {
    const parts = [
      getText(ender, "xLgr"),
      getText(ender, "nro"),
      getText(ender, "xCpl"),
    ].filter(Boolean);

    return parts.join(", ");
  };

  const itens = Array.from(xmlDoc.getElementsByTagName("det")).map((det) => {
    const prod = det.getElementsByTagName("prod")[0];
    const imposto = det.getElementsByTagName("imposto")[0];
    const icmsNode = imposto?.getElementsByTagName("ICMS")[0];
    const ipiNode = imposto?.getElementsByTagName("IPI")[0];

    const icmsGroup = icmsNode
      ? Array.from(icmsNode.children || [])[0]
      : null;

    const ipiTrib = ipiNode
      ? getDirectChild(ipiNode, "IPITrib") || getDirectChild(ipiNode, "IPINT")
      : null;

    return {
      item: det.getAttribute("nItem") ?? "",
      material: getText(prod, "cProd"),
      descriptionMaterial: getText(prod, "xProd"),
      ncm: getText(prod, "NCM"),
      cfop: getText(prod, "CFOP"),
      ucom: getText(prod, "uCom"),
      qty: getText(prod, "qCom"),
      unitValue: getText(prod, "vUnCom"),
      value: getText(prod, "vProd"),
      cst: getText(icmsGroup, "CST") || getText(icmsGroup, "CSOSN"),
      bcIcms: getText(icmsGroup, "vBC"),
      vlrIcms: getText(icmsGroup, "vICMS"),
      aliqIcms: getText(icmsGroup, "pICMS"),
      vlrIpi: getText(ipiTrib, "vIPI"),
      aliqIpi: getText(ipiTrib, "pIPI"),
      order: getText(prod, "xPed"),
      orderItem: getText(prod, "nItemPed"),
    };
  });

  const faturas = Array.from(xmlDoc.getElementsByTagName("dup")).map((dup) => ({
    numero: getText(dup, "nDup"),
    vencimento: getText(dup, "dVenc"),
    valor: getText(dup, "vDup"),
  }));

  return {
    accesskey: infNFe?.getAttribute("Id")?.replace("NFe", "") ?? "",
    versionXml: infNFe?.getAttribute("versao") ?? "",

    ide: {
      number: getText(ide, "nNF"),
      serie: getText(ide, "serie"),
      tpNF: getText(ide, "tpNF"),
      natOp: getText(ide, "natOp"),
      issueDate,
      issueTime,
      outDate,
      outTime,
    },

    emitente: {
      nome: getText(emit, "xNome"),
      cnpj: getText(emit, "CNPJ"),
      cpf: getText(emit, "CPF"),
      ie: getText(emit, "IE"),
      im: getText(emit, "IM"),
      ieSubst: getText(emit, "IEST"),
      endereco: buildAddress(enderEmit),
      bairro: getText(enderEmit, "xBairro"),
      municipio: getText(enderEmit, "xMun"),
      uf: getText(enderEmit, "UF"),
      cep: getText(enderEmit, "CEP"),
      fone: getText(enderEmit, "fone"),
    },

    destinatario: {
      nome: getText(dest, "xNome"),
      cnpj: getText(dest, "CNPJ"),
      cpf: getText(dest, "CPF"),
      ie: getText(dest, "IE"),
      endereco: buildAddress(enderDest),
      bairro: getText(enderDest, "xBairro"),
      municipio: getText(enderDest, "xMun"),
      uf: getText(enderDest, "UF"),
      cep: getText(enderDest, "CEP"),
      fone: getText(enderDest, "fone"),
    },

    protocolo: {
      numero: getText(infProt, "nProt"),
      status: getText(infProt, "cStat"),
    },

    total: {
      baseIcms: getText(total, "vBC"),
      valorIcms: getText(total, "vICMS"),
      baseIcmsSt: getText(total, "vBCST"),
      valorIcmsSt: getText(total, "vST"),
      valorProdutos: getText(total, "vProd"),
      valorFrete: getText(total, "vFrete"),
      valorSeguro: getText(total, "vSeg"),
      desconto: getText(total, "vDesc"),
      outrasDespesas: getText(total, "vOutro"),
      valorIpi: getText(total, "vIPI"),
      valorTributos: getText(total, "vTotTrib"),
      valorNota: getText(total, "vNF"),
      valorImportacao: getText(total, "vII"),
      valorIcmsUfRemet: getText(total, "vICMSUFRemet"),
      valorFcpUfDest: getText(total, "vFCPUFDest"),
      valorIcmsUfDest: getText(total, "vICMSUFDest"),
    },

    transporte: {
      nome: getText(transporta, "xNome"),
      freteConta: getText(transp, "modFrete"),
      codigoAntt: getText(veicTransp, "RNTC"),
      placa: getText(veicTransp, "placa"),
      ufPlaca: getText(veicTransp, "UF"),
      cnpjCpf: getText(transporta, "CNPJ") || getText(transporta, "CPF"),
      endereco: getText(transporta, "xEnder"),
      municipio: getText(transporta, "xMun"),
      uf: getText(transporta, "UF"),
      ie: getText(transporta, "IE"),
      quantidade: getText(vol, "qVol"),
      especie: getText(vol, "esp"),
      marca: getText(vol, "marca"),
      numeracao: getText(vol, "nVol"),
      pesoBruto: getText(vol, "pesoB"),
      pesoLiquido: getText(vol, "pesoL"),
    },

    dadosAdicionais: {
      observacoes: getText(infAdic, "infCpl"),
      reservadoFisco: getText(infAdic, "infAdFisco"),
    },

    faturas,
    itens,
  };
};

export const readXmlFile = (file, t) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (event) => {
      try {
        const xmlText = event.target?.result;
        const parsed = parseNfeXml(xmlText, t);

        resolve({
          fileName: file.name,
          fileSize: file.size,
          rawXml: xmlText,
          ...parsed,
        });
      } catch (err) {
        reject(err);
      }
    };

    reader.onerror = () => {
      reject(new Error(t("message.msg0015")));
    };

    reader.readAsText(file, "utf-8");
  });
};

export const readMultipleXmlFiles = async (files, t) => {
  return Promise.all(Array.from(files || []).map((file) => readXmlFile(file, t)));
};


export const formatBrDate = (date) => {
  if (!date) return "";
  const d = typeof date === "string" ? date.slice(0, 10) : date;
  return dayjs(d).format("DD/MM/YYYY");
};

export const toIsoDate = (date) => {
  if (!date) return "";
  return dayjs(date).format("YYYY-MM-DD");
};
